import React, { useEffect, useMemo, useState } from "react";
import Input from "../ui/Input";
import Button from "../ui/Button";
import * as authTenantActions from "src/redux/slices/authTenantSlice";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { toast } from "react-toastify";

interface CompanyDetailsFormProps {
  handleCancel: () => void;
}

const PREFIX_FIELDS: {
  key:
    | "saleOrderPrefix"
    | "purchaseOrderPrefix"
    | "productPrefix"
    | "adjustmentOrderPrefix"
    | "transactionPrefix"
    | "transferPrefix";
  label: string;
  fallback: string;
}[] = [
  { key: "saleOrderPrefix", label: "Sale Order", fallback: "SO" },
  { key: "purchaseOrderPrefix", label: "Purchase Order", fallback: "PO" },
  { key: "productPrefix", label: "Product", fallback: "P" },
  { key: "adjustmentOrderPrefix", label: "Stock Adjustment", fallback: "ADJ" },
  { key: "transactionPrefix", label: "Inventory Transaction", fallback: "TRN" },
  { key: "transferPrefix", label: "Stock Transfer", fallback: "TRF" },
];

const PREFIX_REGEX = /^[A-Za-z0-9]{1,8}$/;

// Mirrors the backend's generateEntityId format: {prefix}{YY}{DD}{MM}{seq}
const previewId = (prefix: string) => {
  const now = new Date();
  const yy = String(now.getFullYear()).slice(-2);
  const dd = String(now.getDate()).padStart(2, "0");
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  return `${prefix || "?"}${yy}${dd}${mm}001`;
};

const CompanyDetailsForm: React.FC<CompanyDetailsFormProps> = ({
  handleCancel,
}) => {
  const dispatch = useAppDispatch();
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const { editCompanyDetails } = useAppSelector((state) => state.authTenant);
  const tenant = user?.tenantDetails;

  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [pincode, setPincode] = useState("");
  const [gst, setGst] = useState("");
  const [prefixes, setPrefixes] = useState<Record<string, string>>({});
  const [warehouseSingular, setWarehouseSingular] = useState("");
  const [warehousePlural, setWarehousePlural] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!tenant) return;
    setAddressLine1(tenant.addressLine1 || "");
    setAddressLine2(tenant.addressLine2 || "");
    setCity(tenant.city || "");
    setState(tenant.state || "");
    setPincode(tenant.pincode || "");
    setGst(tenant.gst || "");
    setPrefixes({
      saleOrderPrefix: tenant.idPrefixes?.saleOrder || "SO",
      purchaseOrderPrefix: tenant.idPrefixes?.purchaseOrder || "PO",
      productPrefix: tenant.idPrefixes?.product || "P",
      adjustmentOrderPrefix: tenant.idPrefixes?.adjustmentOrder || "ADJ",
      transactionPrefix: tenant.idPrefixes?.transaction || "TRN",
      transferPrefix: tenant.idPrefixes?.transfer || "TRF",
    });
    setWarehouseSingular(tenant.warehouseLabel?.singular || "Warehouse");
    setWarehousePlural(tenant.warehouseLabel?.plural || "Warehouses");
  }, [tenant]);

  useEffect(() => {
    if (editCompanyDetails.data) {
      toast.success("Company details updated successfully");
      dispatch(authTenantActions.getUserDetailsRequest());
      dispatch(authTenantActions.resetEditCompanyDetails());
    } else if (editCompanyDetails.error) {
      toast.error(`Error: ${editCompanyDetails.error}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editCompanyDetails]);

  const prefixErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    PREFIX_FIELDS.forEach(({ key, label }) => {
      const value = prefixes[key] || "";
      if (!PREFIX_REGEX.test(value)) {
        errors[key] = `${label} prefix must be 1-8 letters/numbers`;
      }
    });
    return errors;
  }, [prefixes]);

  const warehouseLabelErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    if (!warehouseSingular.trim()) errors.singular = "Required";
    if (!warehousePlural.trim()) errors.plural = "Required";
    return errors;
  }, [warehouseSingular, warehousePlural]);

  const isValid =
    Object.keys(prefixErrors).length === 0 &&
    Object.keys(warehouseLabelErrors).length === 0;
  const loading = editCompanyDetails.loading;

  const handleSubmit = () => {
    setTouched(true);
    if (!isValid) return;

    try {
      dispatch(
        authTenantActions.editCompanyDetailsRequest({
          gst,
          addressLine1,
          addressLine2,
          city,
          state,
          pincode,
          idPrefixes: {
            saleOrder: prefixes.saleOrderPrefix,
            purchaseOrder: prefixes.purchaseOrderPrefix,
            product: prefixes.productPrefix,
            adjustmentOrder: prefixes.adjustmentOrderPrefix,
            transaction: prefixes.transactionPrefix,
            transfer: prefixes.transferPrefix,
          },
          warehouseLabel: {
            singular: warehouseSingular.trim(),
            plural: warehousePlural.trim(),
          },
        } as any)
      );
    } catch (error: any) {
      toast.error("Failed to update company details");
      console.error(error);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Company details
        </h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Address Line 1"
            optional
            className="sm:col-span-2"
            placeholder="Building, street"
            value={addressLine1}
            onChange={(e) => setAddressLine1(e.target.value)}
          />
          <Input
            label="Address Line 2"
            optional
            className="sm:col-span-2"
            placeholder="Area, landmark"
            value={addressLine2}
            onChange={(e) => setAddressLine2(e.target.value)}
          />
          <Input label="City" optional value={city} onChange={(e) => setCity(e.target.value)} />
          <Input label="State" optional value={state} onChange={(e) => setState(e.target.value)} />
          <Input
            label="Pincode"
            optional
            value={pincode}
            onChange={(e) => setPincode(e.target.value)}
          />
          <Input
            label="GST Number"
            optional
            placeholder="e.g. 29ABCDE1234F1Z5"
            value={gst}
            onChange={(e) => setGst(e.target.value)}
          />
        </div>
      </div>

      <div className="border-t border-gray-100 pt-5 dark:border-gray-700">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          ID Prefixes
        </h3>
        <p className="mb-3 text-xs text-gray-400 dark:text-gray-500">
          Shown at the start of every new Sale, Purchase, Product and other document ID.
        </p>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          {PREFIX_FIELDS.map(({ key, label, fallback }) => {
            const value = prefixes[key] ?? "";
            const error = touched ? prefixErrors[key] : undefined;
            return (
              <Input
                key={key}
                label={label}
                placeholder={fallback}
                value={value}
                maxLength={8}
                inputClassName="font-mono uppercase"
                onChange={(e) =>
                  setPrefixes((prev) => ({ ...prev, [key]: e.target.value }))
                }
                error={!!error}
                helperText={error || `e.g. ${previewId(value || fallback)}`}
              />
            );
          })}
        </div>
      </div>

      <div className="border-t border-gray-100 pt-5 dark:border-gray-700">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Terminology
        </h3>
        <p className="mb-3 text-xs text-gray-400 dark:text-gray-500">
          What you call a warehouse — used everywhere the app refers to one
          (Sale, Purchase, Stock Adjustment, Transfers, Reports, and the
          Inventory menu). Doesn't change how it's stored or referenced.
        </p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Singular"
            placeholder="Warehouse"
            value={warehouseSingular}
            onChange={(e) => setWarehouseSingular(e.target.value)}
            error={touched && !!warehouseLabelErrors.singular}
            helperText={
              touched && warehouseLabelErrors.singular
                ? warehouseLabelErrors.singular
                : "e.g. Warehouse, Store, Location, Branch"
            }
          />
          <Input
            label="Plural"
            placeholder="Warehouses"
            value={warehousePlural}
            onChange={(e) => setWarehousePlural(e.target.value)}
            error={touched && !!warehouseLabelErrors.plural}
            helperText={
              touched && warehouseLabelErrors.plural
                ? warehouseLabelErrors.plural
                : "e.g. Warehouses, Stores, Locations, Branches"
            }
          />
        </div>
      </div>

      <div className="flex justify-end gap-2 border-t border-gray-100 pt-4 dark:border-gray-700">
        <Button type="button" variant="outline" onClick={handleCancel} disabled={loading}>
          Cancel
        </Button>
        <Button type="button" onClick={handleSubmit} isLoading={loading} disabled={loading}>
          Save
        </Button>
      </div>
    </div>
  );
};

export default CompanyDetailsForm;
